/**
 * PushContextHeader — one-glance stakes strip rendered at the top of
 * every Push-Mode modal.
 *
 * Surfaces the 3 numbers that drive the operator's decision:
 *   • Revenue at risk (contract value, redacted for non-financial roles)
 *   • Days since last customer contact (from activityHistory)
 *   • Biggest active blocker (open issue or scheduling gap)
 *
 * Pure presentational — no mutations, no side effects. Safe to drop into
 * any modal without altering existing behavior.
 */
import { useMemo } from "react";
import { DollarSign, Clock, AlertTriangle } from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  openIssues?: Issue[];
  className?: string;
}

function daysSince(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function fmtCurrency(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${n.toFixed(0)}`;
}

export function PushContextHeader({ job, openIssues = [], className }: Props) {
  const fin = useFinancialPermissions();

  const lastContactDays = useMemo(() => {
    const history = job.activityHistory ?? [];
    const touch = [...history]
      .reverse()
      .find((h) => /call|sms|text|email|message|contact|note/i.test(h.action));
    return daysSince(touch?.date);
  }, [job.activityHistory]);

  const biggestBlocker = useMemo(() => {
    const urgent = openIssues.find((i) => i.severity === "Urgent");
    if (urgent) return { label: urgent.issueType, tone: "critical" as const };
    const medium = openIssues.find((i) => i.severity === "Medium");
    if (medium) return { label: medium.issueType, tone: "high" as const };
    if (openIssues.length > 0) {
      return { label: `${openIssues.length} open issue${openIssues.length > 1 ? "s" : ""}`, tone: "medium" as const };
    }
    if (!job.scheduledInstallDate) return { label: "No install date", tone: "medium" as const };
    if (job.assignmentType === "Unassigned" || (!job.assignedCrew && !job.assignedSubcontractor)) {
      return { label: "No crew assigned", tone: "medium" as const };
    }
    return { label: "On track", tone: "ok" as const };
  }, [openIssues, job.scheduledInstallDate, job.assignmentType, job.assignedCrew, job.assignedSubcontractor]);

  const contractValue = job.financials?.contractValue;
  const showRevenue = fin.viewContractValue && typeof contractValue === "number" && contractValue > 0;

  const contactTone =
    lastContactDays === null ? "muted" :
    lastContactDays >= 5 ? "critical" :
    lastContactDays >= 3 ? "high" : "ok";

  return (
    <div className={cn("rounded-md border bg-muted/30 px-3 py-2", className)}>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="flex items-start gap-1.5 min-w-0">
          <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">At Stake</div>
            <div className="font-semibold truncate">
              {showRevenue ? fmtCurrency(contractValue!) : "—"}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0">
          <Clock className={cn(
            "h-3.5 w-3.5 shrink-0 mt-0.5",
            contactTone === "critical" && "text-destructive",
            contactTone === "high" && "text-warning",
            (contactTone === "ok" || contactTone === "muted") && "text-muted-foreground",
          )} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last Contact</div>
            <div className={cn(
              "font-semibold truncate",
              contactTone === "critical" && "text-destructive",
              contactTone === "high" && "text-warning",
            )}>
              {lastContactDays === null ? "No record" : lastContactDays === 0 ? "Today" : `${lastContactDays}d ago`}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0">
          <AlertTriangle className={cn(
            "h-3.5 w-3.5 shrink-0 mt-0.5",
            biggestBlocker.tone === "critical" && "text-destructive",
            biggestBlocker.tone === "high" && "text-warning",
            biggestBlocker.tone === "medium" && "text-muted-foreground",
            biggestBlocker.tone === "ok" && "text-success",
          )} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Top Blocker</div>
            <div className={cn(
              "font-semibold truncate",
              biggestBlocker.tone === "critical" && "text-destructive",
              biggestBlocker.tone === "high" && "text-warning",
              biggestBlocker.tone === "ok" && "text-success",
            )}>
              {biggestBlocker.label}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
